import jsQR from 'jsqr'

/**
 * Membaca payload QRIS dari gambar QR (foto, screenshot, atau file cetak).
 *
 * Decoding sengaja dilakukan di BROWSER, bukan di Worker: workerd tidak punya
 * canvas maupun decoder JPEG/PNG, jadi versi server perlu WASM image-decode +
 * WASM QR-detect hanya untuk satu form admin. Yang dikirim ke server tetap
 * payload teksnya, sehingga endpoint PUT /admin/qris (beserta validasi CRC-nya)
 * tidak berubah sama sekali.
 */

/**
 * Sisi terpanjang untuk percobaan pertama. Foto QRIS dari HP biasanya
 * 3000–4000 px; segitu hanya memperlambat jsQR tanpa menambah ketelitian.
 */
const SISI_AWAL_MAKS = 1600

/**
 * Di bawah ini modul QR mulai menyatu dan justru merusak pembacaan, jadi
 * tangga berhenti walaupun percobaan sebelumnya gagal.
 */
const SISI_MINIMUM = 220

/**
 * Faktor pengecilan yang dicoba berurutan, RELATIF terhadap ukuran gambar.
 *
 * Harus relatif, bukan daftar piksel mutlak: dengan angka mutlak, gambar yang
 * sudah kecil (mis. screenshot QR 550 px) melewati semua ukuran alternatif
 * karena semuanya lebih besar dari gambarnya — tangganya jadi cuma satu anak
 * dan tidak pernah menolong. Padahal justru pengecilan itu yang menyelamatkan
 * gambar berderau: merata-ratakan blok piksel menekan derau frekuensi tinggi
 * yang membuat binarisasi jsQR meleset.
 */
const FAKTOR_DICOBA = [1, 0.6, 0.4, 0.25]

/** Batas wajar supaya tidak menggantung tab kalau user memilih file raksasa. */
const MAKS_BYTE = 25 * 1024 * 1024

export class QrTidakTerbaca extends Error {}

/** Piksel RGBA — sengaja tidak memakai tipe DOM `ImageData` supaya inti
 *  algoritmanya bisa diuji tanpa browser. */
export interface PikselRgba {
  data: Uint8ClampedArray
  width: number
  height: number
}

/**
 * Inti pembacaan: coba beberapa ukuran sampai ada yang terbaca.
 *
 * `ambil` menyediakan piksel pada sisi terpanjang tertentu — di browser ia
 * menggambar ulang lewat canvas, di test ia meratakan piksel langsung. Yang
 * penting: tangga ukuran ini bukan hiasan. Foto QR dari kamera membawa derau
 * frekuensi tinggi yang membuat binarisasi jsQR meleset pada resolusi penuh,
 * dan memperkecil gambar merata-ratakan derau itu sampai QR-nya terbaca.
 */
export function decodeDenganTurunSkala(
  ambil: (sisiMaks: number) => PikselRgba | null,
  sisiAsli: number,
): string {
  const dasar = Math.min(sisiAsli, SISI_AWAL_MAKS)
  const dicoba = new Set<number>()

  for (const faktor of FAKTOR_DICOBA) {
    const sisi = Math.round(dasar * faktor)
    // Berhenti mengecil sebelum modul QR menyatu; ukuran kembar dilewati.
    if (sisi < SISI_MINIMUM && dicoba.size > 0) break
    if (dicoba.has(sisi)) continue
    dicoba.add(sisi)

    const img = ambil(sisi)
    if (!img) throw new QrTidakTerbaca('The browser refused to create a canvas to read the image')

    // attemptBoth: sebagian QR dicetak/di-screenshot terbalik (modul terang di
    // atas latar gelap), dan itu tetap QR yang sah.
    const hasil = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' })
    if (hasil?.data) return hasil.data
  }

  throw new QrTidakTerbaca(
    "QR couldn't be read from this image. Make sure the whole QR square is in frame, not blurry, and not covered by glare.",
  )
}

function gambarKeImageData(bitmap: ImageBitmap, sisiMaks: number): ImageData | null {
  const skala = Math.min(1, sisiMaks / Math.max(bitmap.width, bitmap.height))
  const w = Math.max(1, Math.round(bitmap.width * skala))
  const h = Math.max(1, Math.round(bitmap.height * skala))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  // willReadFrequently: kita memang hanya membaca piksel, bukan menggambar ulang.
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  ctx.drawImage(bitmap, 0, 0, w, h)
  return ctx.getImageData(0, 0, w, h)
}

export async function decodeQrDariGambar(file: File | Blob): Promise<string> {
  if (file.size > MAKS_BYTE) {
    throw new QrTidakTerbaca('Image is too large (25 MB maximum)')
  }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    // Penyebab tersering: HEIC dari iPhone, yang tidak bisa didecode browser.
    throw new QrTidakTerbaca(
      "The image format isn't supported by the browser. Try saving it again as JPG or PNG (iPhone photos in HEIC format need converting).",
    )
  }

  try {
    return decodeDenganTurunSkala(
      (sisi) => gambarKeImageData(bitmap, sisi),
      Math.max(bitmap.width, bitmap.height),
    )
  } finally {
    bitmap.close()
  }
}

/**
 * Tebakan cepat apakah teks hasil scan memang payload EMVCo/QRIS.
 *
 * Bukan pengganti validasi server (CRC tetap dicek di backend) — gunanya
 * membedakan "QR-nya terbaca tapi isinya bukan QRIS" dari "payload QRIS rusak".
 * Merchant kadang memotret QR yang salah, mis. QR berisi tautan menu, dan pesan
 * "CRC tidak valid" untuk kasus itu menyesatkan.
 */
export function tampakSepertiQris(teks: string): boolean {
  const t = teks.trim()
  // Diawali tag 00 (Payload Format Indicator) lalu tag 01, dan memuat tag 58
  // negara "ID" — tiga penanda yang selalu ada di QRIS Indonesia.
  return /^00\d{2}01\d{2}/.test(t) && t.includes('5802ID')
}

/**
 * Mengambil berkas gambar pertama dari event paste — mendukung alur paling
 * natural untuk QR statis: screenshot lalu Ctrl+V.
 */
export function gambarDariPaste(e: ClipboardEvent | React.ClipboardEvent): File | null {
  const items = (e as ClipboardEvent).clipboardData?.items
  if (!items) return null
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) return file
    }
  }
  return null
}
